import { Application, BlurFilter, Graphics } from "pixi.js";
import { Viewport } from "pixi-viewport";
import { useCallback, useEffect, useRef } from "react";
import { uuidv7 } from "uuidv7";
import * as Y from "yjs";

import { GridRenderer } from "@/features/battlemap/components/GridRenderer";
import {
  BattlemapSettings,
  ContextMenuAction,
  DrawingPath,
  DrawingShape,
  FogRoom,
  FogShape,
  Wall,
  WallSegment,
} from "@/features/battlemap/types";
import { useBattlemapStore } from "@/stores/useBattlemapStore";

import { calculateFogFill } from "../../../shared/lib/floodFill";
import {
  BRUSH_MIN_DISTANCE,
  DEFAULT_DRAW_FILL_ALPHA,
  DEFAULT_FILL_ALPHA,
  DEFAULT_FILL_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_ROOM_COLOR,
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
  DEFAULT_TEXT_CONTENT,
  DEFAULT_TEXT_WIDTH,
  DRAG_TOLERANCE,
  DRAW_BRUSH_MIN_DISTANCE,
  FOG_BRUSH_ALPHA,
  FOG_BRUSH_STROKE_WIDTH,
  FOG_FILL_ALPHA,
  FOG_FILL_RESOLUTION,
  FOG_HIDE_FILL_COLOR,
  FOG_HIDE_STROKE_COLOR,
  FOG_REVEAL_FILL_COLOR,
  FOG_REVEAL_STROKE_COLOR,
  FOG_SIZE,
  FOG_STROKE_ALPHA,
  HANDLE_BOTTOM_LEFT,
  HANDLE_BOTTOM_RIGHT,
  HANDLE_HIT_SIZE,
  HANDLE_TOP_LEFT,
  HANDLE_TOP_RIGHT,
  MARQUEE_FILL_ALPHA,
  MARQUEE_STROKE_ALPHA,
  MIN_RECT_SIZE,
  MIN_SHAPE_SIZE,
  MIN_TEXT_WIDTH,
  POLYGON_CLOSE_DISTANCE,
  POLYGON_MIN_POINTS,
  SELECTION_COLOR,
  TEXT_HEIGHT_RATIO,
} from "../lib/constants";
import { boundsToRect, getDrawingBounds, normalizeRect } from "../lib/geometry";
import {
  hitTestDrawings,
  marqueeSelect,
  pointInDrawing,
  pointInHandle,
} from "../lib/hitTest";
import { deleteYArrayItem } from "../lib/yjsUtils";

function hexToNumber(hexColor: string, defaultColorHex: string): number {
  if (!hexColor) {
    return parseInt(defaultColorHex.replace("#", ""), 16);
  }
  const cleanHex = hexColor.replace("#", "");
  const result = parseInt(cleanHex, 16);
  return Number.isNaN(result)
    ? parseInt(defaultColorHex.replace("#", ""), 16)
    : result;
}

function snapToGridCorner(
  x: number,
  y: number,
  gridType: string,
  cellSize: number,
  offsetX = 0,
  offsetY = 0
): { x: number; y: number } {
  if (gridType === "none") {
    return { x, y };
  }

  if (gridType === "square") {
    const cellX = Math.floor((x - offsetX) / cellSize);
    const cellY = Math.floor((y - offsetY) / cellSize);
    return {
      x: cellX * cellSize + offsetX,
      y: cellY * cellSize + offsetY,
    };
  }

  return GridRenderer.snapToGrid(
    x,
    y,
    gridType as any,
    cellSize,
    offsetX,
    offsetY
  );
}

interface UseBattlemapInteractionsProps {
  app: Application | null;
  viewport: Viewport | null;
  doc: Y.Doc;
  fogArray: Y.Array<FogShape>;
  drawingsArray: Y.Array<DrawingPath>;
  wallsArray: Y.Array<Wall>;
  roomsArray: Y.Array<FogRoom>;
  settings: BattlemapSettings;
  previewGraphicsRef: React.MutableRefObject<Graphics | null>;
  onOpenContextMenu: (
    x: number,
    y: number,
    actions: ContextMenuAction[]
  ) => void;
}

export function useBattlemapInteractions({
  app,
  viewport,
  doc,
  fogArray,
  drawingsArray,
  wallsArray,
  roomsArray,
  settings,
  previewGraphicsRef,
  onOpenContextMenu,
}: UseBattlemapInteractionsProps) {
  const isSpacePressedRef = useRef(false);
  const isMiddlePressedRef = useRef(false);
  const isSelectButtonPressedRef = useRef(false);
  /* eslint-disable react-compiler/react-compiler */
  const middleDragStartRef = useRef<{ x: number; y: number } | null>(null);
  const resizeHandleRef = useRef<string | null>(null);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const marqueeStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialResizeStateRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null); // For dragging items

  // Store access
  const activeTool = useBattlemapStore((s) => s.activeTool);
  const fogTool = useBattlemapStore((s) => s.fogTool);
  const drawTool = useBattlemapStore((s) => s.drawTool);
  const fogMode = useBattlemapStore((s) => s.fogMode);
  const activeRoomId = useBattlemapStore((s) => s.activeRoomId);
  const brushSize = useBattlemapStore((s) => s.brushSize);
  const isDrawing = useBattlemapStore((s) => s.isDrawing);
  const setIsDrawing = useBattlemapStore((s) => s.setIsDrawing);
  const setCurrentPath = useBattlemapStore((s) => s.setCurrentPath);
  const appendToCurrentPath = useBattlemapStore((s) => s.appendToCurrentPath);
  const currentPath = useBattlemapStore((s) => s.currentPath);
  const clearCurrentPath = useBattlemapStore((s) => s.clearCurrentPath);
  const selectedDrawingIds = useBattlemapStore((s) => s.selectedDrawingIds);
  const setSelectedDrawingIds = useBattlemapStore(
    (s) => s.setSelectedDrawingIds
  );
  const setIsDraggingDrawing = useBattlemapStore((s) => s.setIsDraggingDrawing);
  const isDraggingDrawing = useBattlemapStore((s) => s.isDraggingDrawing);
  const drawingProps = useBattlemapStore((s) => s.drawingProps);
  const setActiveTool = useBattlemapStore((s) => s.setActiveTool);
  const setEditingItemId = useBattlemapStore((s) => s.setEditingItemId);

  // Keyboard events for Space panning
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space" && !isSpacePressedRef.current) {
        if (
          ["INPUT", "TEXTAREA"].includes(
            (document.activeElement as HTMLElement)?.tagName
          )
        )
          return;

        isSpacePressedRef.current = true;

        // Enable temporary pan mode (updates toolbar)
        const { enableTemporaryPan } = useBattlemapStore.getState();
        enableTemporaryPan();

        if (viewport) {
          viewport.plugins?.resume("drag");
        }
      }

      if (
        (e.code === "Delete" || e.code === "Backspace") &&
        useBattlemapStore.getState().activeTool === "select"
      ) {
        if (
          ["INPUT", "TEXTAREA"].includes(
            (document.activeElement as HTMLElement)?.tagName
          )
        )
          return;

        const { selectedDrawingIds, setSelectedDrawingIds } =
          useBattlemapStore.getState();

        if (selectedDrawingIds.length > 0) {
          doc.transact(() => {
            const items = drawingsArray.toArray();

            // Map to indices first, then sort descending to remove safely
            const indicesToDelete = items
              .map((d, i) => ({ id: d.id, index: i }))
              .filter((item) => selectedDrawingIds.includes(item.id))
              .map((item) => item.index)
              .sort((a, b) => b - a);

            indicesToDelete.forEach((index) => {
              drawingsArray.delete(index, 1);
            });
          });
          setSelectedDrawingIds([]);
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        isSpacePressedRef.current = false;

        // Disable temporary pan mode (restore previous tool in toolbar)
        const { disableTemporaryPan } = useBattlemapStore.getState();
        disableTemporaryPan();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [viewport, doc, drawingsArray]);

  // Toggle drag based on tool
  useEffect(() => {
    if (!viewport) return;

    // Resume drag if:
    // 1. Tool is PAN
    // 2. Spacebar is held (temporary pan)
    // 3. Middle mouse button is held (temporary pan)

    if (
      activeTool === "pan" ||
      isSpacePressedRef.current ||
      isMiddlePressedRef.current
    ) {
      viewport.plugins?.resume("drag");
    } else {
      viewport.plugins?.pause("drag");
    }
  }, [activeTool, viewport]);

  // Helper to render preview
  const updatePreview = useCallback(
    (x: number, y: number, shouldSnapOverride?: boolean) => {
      const g = previewGraphicsRef.current;
      if (!g) return;

      g.clear();
      g.alpha = 1;
      g.filters = null;

      const {
        activeTool,
        fogTool,
        drawTool,
        fogMode,
        brushSize,
        isDrawing,
        currentPath,
        drawingProps,
        selectedDrawingIds,
      } = useBattlemapStore.getState();

      // For rect/ellipse tools, use current x,y for preview end position
      // This ensures preview is responsive to snapped cursor position
      const isRectEllipseDrawing =
        isDrawing &&
        ((activeTool === "fog" &&
          (fogTool === "rect" || fogTool === "ellipse")) ||
          (activeTool === "draw" &&
            (drawTool === "rect" || drawTool === "ellipse")));

      const previewEndX = isRectEllipseDrawing ? x : currentPath[2];
      const previewEndY = isRectEllipseDrawing ? y : currentPath[3];

      if (activeTool === "select") {
        g.clear();

        // Only draw marquee preview here - selection bounds are drawn by useDrawingRenderer
        const isMarqueeSelecting =
          marqueeStartRef.current &&
          isSelectButtonPressedRef.current &&
          selectedDrawingIds.length === 0;

        if (isMarqueeSelecting) {
          // Draw Marquee Selection Preview
          const marqueeStart = marqueeStartRef.current;
          if (!marqueeStart) return;

          const shouldSnap = shouldSnapOverride ?? settings.snapToGrid;

          // Apply snapping to end position if enabled
          let endX = x;
          let endY = y;
          if (shouldSnap) {
            const snapped = snapToGridCorner(
              x,
              y,
              settings.gridType,
              settings.gridCellSize,
              settings.gridOffsetX || 0,
              settings.gridOffsetY || 0
            );
            endX = snapped.x;
            endY = snapped.y;
          }

          const startX = marqueeStart.x;
          const startY = marqueeStart.y;
          const {
            x: bx,
            y: by,
            width: bw,
            height: bh,
          } = normalizeRect(startX, startY, endX, endY);

          g.rect(bx, by, bw, bh);
          g.fill({ color: SELECTION_COLOR, alpha: MARQUEE_FILL_ALPHA });
          g.stroke({
            color: SELECTION_COLOR,
            width: 1,
            alpha: MARQUEE_STROKE_ALPHA,
          });
        }
        return;
      }

      if (activeTool === "fog") {
        // Preview Style Configuration
        const isHide = fogMode === "hide";
        const fillColor = isHide ? FOG_HIDE_FILL_COLOR : FOG_REVEAL_FILL_COLOR;
        const strokeColor = isHide
          ? FOG_HIDE_STROKE_COLOR
          : FOG_REVEAL_STROKE_COLOR;

        if (fogTool === "fill") {
          g.rect(0, 0, FOG_SIZE, FOG_SIZE);
          g.fill({ color: fillColor, alpha: FOG_FILL_ALPHA });
          g.stroke({
            color: strokeColor,
            width: FOG_BRUSH_STROKE_WIDTH,
            alpha: FOG_STROKE_ALPHA,
          });
        } else if (fogTool === "brush") {
          g.circle(x, y, brushSize / 2);
          g.fill({ color: fillColor, alpha: FOG_BRUSH_ALPHA });
          g.stroke({ color: strokeColor, width: 1, alpha: FOG_STROKE_ALPHA });

          if (isDrawing && currentPath.length >= 2) {
            g.moveTo(currentPath[0], currentPath[1]);
            for (let i = 2; i < currentPath.length; i += 2) {
              g.lineTo(currentPath[i], currentPath[i + 1]);
            }
            g.lineTo(x, y);
            g.stroke({
              width: brushSize,
              color: fillColor,
              alpha: FOG_FILL_ALPHA,
              cap: "round",
              join: "round",
            });
          }
        } else if (fogTool === "grid") {
          const shape = GridRenderer.getCellShape(
            x,
            y,
            settings.gridType,
            settings.gridCellSize,
            settings.gridOffsetX || 0,
            settings.gridOffsetY || 0
          );

          if (shape) {
            if (shape.type === "rect") {
              const [rx, ry, rw, rh] = shape.data;
              g.rect(rx, ry, rw, rh);
            } else if (shape.type === "poly") {
              g.poly(shape.data);
            }
            g.fill({ color: fillColor, alpha: FOG_FILL_ALPHA });
            g.stroke({
              color: strokeColor,
              width: FOG_BRUSH_STROKE_WIDTH,
              alpha: FOG_STROKE_ALPHA,
            });
          }
        } else if (isDrawing) {
          if (fogTool === "rect") {
            const {
              x: rectX,
              y: rectY,
              width: rectW,
              height: rectH,
            } = normalizeRect(
              currentPath[0],
              currentPath[1],
              previewEndX,
              previewEndY
            );

            g.rect(rectX, rectY, rectW, rectH);
            g.fill({ color: fillColor, alpha: FOG_FILL_ALPHA });
            g.stroke({
              color: strokeColor,
              width: FOG_BRUSH_STROKE_WIDTH,
              alpha: FOG_STROKE_ALPHA,
            });
          } else if (fogTool === "ellipse") {
            const startX = currentPath[0];
            const startY = currentPath[1];
            const width = Math.abs(previewEndX - startX);
            const height = Math.abs(previewEndY - startY);
            const centerX = Math.min(startX, previewEndX) + width / 2;
            const centerY = Math.min(startY, previewEndY) + height / 2;

            g.ellipse(centerX, centerY, width / 2, height / 2);
            g.fill({ color: fillColor, alpha: FOG_FILL_ALPHA });
            g.stroke({
              color: strokeColor,
              width: FOG_BRUSH_STROKE_WIDTH,
              alpha: FOG_STROKE_ALPHA,
            });
          } else if (fogTool === "polygon") {
            if (currentPath.length >= 2) {
              g.moveTo(currentPath[0], currentPath[1]);
              for (let i = 2; i < currentPath.length; i += 2) {
                g.lineTo(currentPath[i], currentPath[i + 1]);
              }
              g.lineTo(x, y);
              g.stroke({
                color: strokeColor,
                width: FOG_BRUSH_STROKE_WIDTH,
                alpha: FOG_STROKE_ALPHA,
              });
              g.fill({ color: fillColor, alpha: FOG_BRUSH_ALPHA });
            }
          }
        }
      } else if (activeTool === "draw") {
        const {
          strokeColor,
          strokeWidth,
          fillColor,
          fillAlpha,
          opacity,
          blur,
        } = drawingProps;

        g.alpha = opacity ?? 1;
        if (blur && blur > 0) {
          const blurFilter = new BlurFilter();
          blurFilter.blur = blur;
          g.filters = [blurFilter];
        } else {
          g.filters = null;
        }

        const strokeColorNum = hexToNumber(strokeColor, DEFAULT_STROKE_COLOR);
        const fillColorNum = hexToNumber(fillColor, DEFAULT_FILL_COLOR);

        if (drawTool === "brush") {
          g.circle(x, y, (strokeWidth || 2) / 2);
          g.fill({ color: strokeColorNum, alpha: 1 });
          if (isDrawing && currentPath.length >= 2) {
            g.moveTo(currentPath[0], currentPath[1]);
            for (let i = 2; i < currentPath.length; i += 2) {
              g.lineTo(currentPath[i], currentPath[i + 1]);
            }
            g.lineTo(x, y);
            g.stroke({
              color: strokeColorNum,
              width: strokeWidth || 2,
              alpha: 1,
              cap: "round",
              join: "round",
            });
          }
        } else if (drawTool === "rect") {
          if (isDrawing) {
            const startX = currentPath[0];
            const startY = currentPath[1];
            const rectX = Math.min(startX, previewEndX);
            const rectY = Math.min(startY, previewEndY);
            const rectW = Math.abs(previewEndX - startX);
            const rectH = Math.abs(previewEndY - startY);
            g.rect(rectX, rectY, rectW, rectH);
            g.stroke({
              color: strokeColorNum,
              width: strokeWidth || 2,
              alpha: 1,
            });
            g.fill({ color: fillColorNum, alpha: fillAlpha ?? 0.1 });
          }
        } else if (drawTool === "ellipse") {
          if (isDrawing) {
            const startX = currentPath[0];
            const startY = currentPath[1];
            const width = Math.abs(previewEndX - startX);
            const height = Math.abs(previewEndY - startY);
            const centerX = Math.min(startX, previewEndX) + width / 2;
            const centerY = Math.min(startY, previewEndY) + height / 2;

            g.ellipse(centerX, centerY, width / 2, height / 2);
            g.stroke({
              color: strokeColorNum,
              width: strokeWidth || 2,
              alpha: 1,
            });
            g.fill({ color: fillColorNum, alpha: fillAlpha ?? 0.1 });
          }
        } else if (drawTool === "polygon") {
          if (isDrawing && currentPath.length >= 2) {
            g.moveTo(currentPath[0], currentPath[1]);
            for (let i = 2; i < currentPath.length; i += 2) {
              g.lineTo(currentPath[i], currentPath[i + 1]);
            }
            g.lineTo(x, y);
            g.stroke({
              color: strokeColorNum,
              width: strokeWidth || 2,
              alpha: 1,
            });
            g.fill({ color: fillColorNum, alpha: fillAlpha ?? 0.1 });
          }
        }
      }
    },
    [
      settings.gridCellSize,
      settings.gridType,
      settings.gridOffsetX,
      settings.gridOffsetY,
      previewGraphicsRef,
      drawingsArray, // Y.Array is stable ref
      // Removed activeTool, fogTool, etc from dependencies
    ]
  );

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (!viewport) return;

      // Allow middle button to pass through for drag (handled by mousedown listener)
      if (e.button === 1) return;

      const {
        activeTool,
        fogTool,
        drawTool,
        fogMode,
        isDrawing,
        setIsDrawing,
        setCurrentPath,
        appendToCurrentPath,
        currentPath,
        clearCurrentPath,
        selectedDrawingIds,
        setSelectedDrawingIds,
      } = useBattlemapStore.getState();

      // Allow middle button to pass through for drag (handled by mousedown listener)
      if (e.button === 1) return;

      const point = viewport.toLocal({
        x: e.offsetX,
        y: e.offsetY,
        clientX: e.clientX,
        clientY: e.clientY,
      } as any);
      let { x, y } = point;

      // Snap logic (Magnetic Snap)
      // Toggle snap if Ctrl is pressed (invert setting)
      // Exception: Grid tool and Fill tool handle their own logic/don't need standard snap
      const shouldSnap = e.ctrlKey ? !settings.snapToGrid : settings.snapToGrid;

      // Snap for fog tool
      if (
        shouldSnap &&
        activeTool === "fog" &&
        fogTool !== "grid" &&
        fogTool !== "fill"
      ) {
        if (fogTool === "rect" || fogTool === "ellipse") {
          const snapped = snapToGridCorner(
            x,
            y,
            settings.gridType,
            settings.gridCellSize,
            settings.gridOffsetX || 0,
            settings.gridOffsetY || 0
          );
          x = snapped.x;
          y = snapped.y;
        } else {
          const snapped = GridRenderer.snapToGrid(
            x,
            y,
            settings.gridType,
            settings.gridCellSize,
            settings.gridOffsetX || 0,
            settings.gridOffsetY || 0
          );
          x = snapped.x;
          y = snapped.y;
        }
      }

      // Snap for select tool marquee start position
      if (shouldSnap && activeTool === "select") {
        const snapped = snapToGridCorner(
          x,
          y,
          settings.gridType,
          settings.gridCellSize,
          settings.gridOffsetX || 0,
          settings.gridOffsetY || 0
        );
        x = snapped.x;
        y = snapped.y;
      }

      // Snap for draw tool
      if (
        shouldSnap &&
        activeTool === "draw" &&
        (drawTool === "rect" || drawTool === "ellipse")
      ) {
        const snapped = snapToGridCorner(
          x,
          y,
          settings.gridType,
          settings.gridCellSize,
          settings.gridOffsetX || 0,
          settings.gridOffsetY || 0
        );
        x = snapped.x;
        y = snapped.y;
      }

      // RIGHT CLICK -> Context Menu (handle before other button checks)
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();

        // Hit Test for Drawings
        const items = drawingsArray.toArray();
        const hitId = hitTestDrawings(x, y, items, {
          layerId: settings.activeLayerId || "map",
        });

        const actions: ContextMenuAction[] = [];
        if (hitId) {
          actions.push({
            id: "delete",
            label: "Delete",
            onClick: () => {
              deleteYArrayItem(drawingsArray, hitId);
            },
            danger: true,
          });
        } else {
          actions.push({
            id: "reset-view",
            label: "Reset View",
            onClick: () => viewport?.setZoom(1),
          });
        }

        if (actions.length > 0) {
          onOpenContextMenu(e.clientX, e.clientY, actions);
        }
        return;
      }

      // Tools only work with left button
      if (isSpacePressedRef.current || e.button !== 0) return;

      // Track left button press for select tool marquee visibility
      if (e.button === 0 && activeTool === "select") {
        isSelectButtonPressedRef.current = true;
      }

      if (activeTool === "fog") {
        if (fogTool === "fill") {
          const segments: WallSegment[] = [];
          if (wallsArray) {
            wallsArray.forEach((w) => segments.push(...w.segments));
          }

          const width = settings.mapWidth || FOG_SIZE;
          const height = settings.mapHeight || FOG_SIZE;
          const bounds = { x: 0, y: 0, width, height };

          const poly = calculateFogFill(
            x,
            y,
            segments,
            bounds,
            FOG_FILL_RESOLUTION
          );

          if (poly && poly.length >= 6) {
            doc.transact(() => {
              const shapeId = uuidv7();
              fogArray.push([
                {
                  id: shapeId,
                  type: "poly",
                  data: poly,
                  operation: fogMode === "hide" ? "add" : "sub",
                },
              ]);

              // Auto-create Room if none active and we are revealing
              let targetRoomId = activeRoomId;
              if (!targetRoomId && fogMode === "reveal" && roomsArray) {
                targetRoomId = uuidv7();
                const newRoom: FogRoom = {
                  id: targetRoomId,
                  name: `Room ${roomsArray.length + 1}`,
                  color: DEFAULT_ROOM_COLOR,
                  shapeIds: [shapeId],
                  doorIds: [],
                  bounds: poly,
                  visible: true,
                  isRevealed: true,
                };
                roomsArray.push([newRoom]);

                // Auto-link doors within room boundaries
                linkDoorsToRoom(
                  targetRoomId,
                  poly,
                  wallsArray,
                  roomsArray,
                  doc
                );
              }

              // Link to Room
              if (targetRoomId && fogMode === "reveal" && roomsArray) {
                const currentRooms = roomsArray.toArray();
                const roomIndex = currentRooms.findIndex(
                  (r) => r.id === targetRoomId
                );
                if (roomIndex !== -1) {
                  const room = currentRooms[roomIndex];
                  const updated = {
                    ...room,
                    shapeIds: [...(room.shapeIds || []), shapeId],
                  };
                  roomsArray.delete(roomIndex, 1);
                  roomsArray.insert(roomIndex, [updated]);
                }
              }
            });
          }
          return;
        } else if (fogTool === "grid") {
          const shape = GridRenderer.getCellShape(
            x,
            y,
            settings.gridType,
            settings.gridCellSize,
            settings.gridOffsetX || 0,
            settings.gridOffsetY || 0
          );

          if (shape) {
            doc.transact(() => {
              fogArray.push([
                {
                  id: uuidv7(),
                  type: shape.type, // 'rect' or 'poly'
                  data: shape.data,
                  operation: fogMode === "hide" ? "add" : "sub",
                },
              ]);
            });
          }
        } else if (fogTool === "polygon") {
          if (!isDrawing) {
            setIsDrawing(true);
            setCurrentPath([x, y]);
          } else {
            appendToCurrentPath(x, y);
          }
        } else {
          setIsDrawing(true);
          if (fogTool === "brush") {
            setCurrentPath([x, y]);
          } else {
            // Rect/Ellipse
            setCurrentPath([x, y, x, y]);
          }
        }
      } else if (activeTool === "draw") {
        // Snap logic for draw tool
        const shouldSnapDraw = e.ctrlKey
          ? !settings.snapToGrid
          : settings.snapToGrid;
        if (shouldSnapDraw && (drawTool === "rect" || drawTool === "ellipse")) {
          const snapped = snapToGridCorner(
            x,
            y,
            settings.gridType,
            settings.gridCellSize,
            settings.gridOffsetX || 0,
            settings.gridOffsetY || 0
          );
          x = snapped.x;
          y = snapped.y;
        }

        if (drawTool === "text") {
          doc.transact(() => {
            const id = uuidv7();
            drawingsArray.push([
              {
                id: id,
                type: "text",
                x,
                y,
                content: DEFAULT_TEXT_CONTENT,
                fontSize: DEFAULT_FONT_SIZE,
                width: DEFAULT_TEXT_WIDTH,
                fontFamily: "Arial",
                strokeColor: "#ffffff",
                layer: "map",
              },
            ]);
            // Auto Select
            useBattlemapStore.getState().setActiveTool("select");
            useBattlemapStore.getState().setSelectedDrawingIds([id]);
          });
        } else if (drawTool === "polygon") {
          if (!isDrawing) {
            setIsDrawing(true);
            setCurrentPath([x, y]);
          } else {
            // Check if clicking near start point to close loop
            if (currentPath.length >= 2) {
              const startX = currentPath[0];
              const startY = currentPath[1];
              const dist = Math.hypot(x - startX, y - startY);
              if (
                dist < POLYGON_CLOSE_DISTANCE &&
                currentPath.length >= POLYGON_MIN_POINTS
              ) {
                // Finish
                const { drawingProps } = useBattlemapStore.getState();
                const {
                  strokeColor,
                  strokeWidth,
                  fillColor,
                  fillAlpha,
                  opacity,
                  blur,
                } = drawingProps;
                const newId = uuidv7();

                doc.transact(() => {
                  drawingsArray.push([
                    {
                      id: newId,
                      type: "polygon",
                      points: [...currentPath],
                      x: 0,
                      y: 0,
                      strokeColor: strokeColor || "#ff0000",
                      strokeWidth: strokeWidth || 2,
                      fillColor: fillColor || "#000000",
                      fillAlpha: fillAlpha ?? 0.1,
                      layer: settings.activeLayerId || "map",
                      opacity: opacity ?? 1,
                      blur: blur ?? 0,
                    },
                  ]);
                });
                setIsDrawing(false);
                clearCurrentPath();
                if (previewGraphicsRef.current)
                  previewGraphicsRef.current.clear();

                useBattlemapStore.getState().setActiveTool("select");
                useBattlemapStore.getState().setSelectedDrawingIds([newId]);
                return;
              }
            }
            appendToCurrentPath(x, y);
          }
        } else {
          // Brush, Rect, Ellipse
          setIsDrawing(true);
          if (drawTool === "brush") {
            setCurrentPath([x, y]);
          } else {
            // Rect/Ellipse initialization
            setCurrentPath([x, y, x, y]);
          }
        }
      } else if (activeTool === "select") {
        // Selection Logic
        let hitId: string | null = null;
        let hitHandle: string | null = null;

        // Only allow resize if EXACTLY ONE item is selected
        if (selectedDrawingIds.length === 1) {
          const selectedDrawingId = selectedDrawingIds[0];
          const selectedItem = drawingsArray
            .toArray()
            .find((d) => d.id === selectedDrawingId);

          if (
            selectedItem &&
            (selectedItem.type === "rect" ||
              selectedItem.type === "ellipse" ||
              selectedItem.type === "text")
          ) {
            const handleSize = HANDLE_HIT_SIZE / viewport.scaled;
            const bounds = getDrawingBounds(selectedItem);

            const handles = [
              { id: HANDLE_TOP_LEFT, x: bounds.minX, y: bounds.minY },
              { id: HANDLE_TOP_RIGHT, x: bounds.maxX, y: bounds.minY },
              { id: HANDLE_BOTTOM_LEFT, x: bounds.minX, y: bounds.maxY },
              { id: HANDLE_BOTTOM_RIGHT, x: bounds.maxX, y: bounds.maxY },
            ];

            for (const h of handles) {
              if (pointInHandle(x, y, h.x, h.y, handleSize)) {
                hitHandle = h.id;
                hitId = selectedItem.id;
                // Capture initial state for stable resizing
                initialResizeStateRef.current = boundsToRect(bounds);
                break;
              }
            }
          }
        }

        if (hitHandle) {
          if (previewGraphicsRef.current) {
            previewGraphicsRef.current.clear();
          }
          resizeHandleRef.current = hitHandle;
          useBattlemapStore.getState().setIsDraggingDrawing(true); // Reuse drag flag for interaction state
          dragStartPosRef.current = { x, y };
        } else {
          // Normal selection/move
          resizeHandleRef.current = null;
          initialResizeStateRef.current = null;

          // Iterate in reverse to select top-most
          const items = drawingsArray.toArray();
          hitId = hitTestDrawings(x, y, items);

          const isShift = e.shiftKey;
          const isAlreadySelected = hitId && selectedDrawingIds.includes(hitId);

          if (hitId) {
            if (previewGraphicsRef.current) {
              previewGraphicsRef.current.clear();
            }
            if (isShift) {
              if (isAlreadySelected) {
                setSelectedDrawingIds(
                  selectedDrawingIds.filter((id) => id !== hitId)
                );
              } else {
                setSelectedDrawingIds([...selectedDrawingIds, hitId]);
              }
            } else if (!isAlreadySelected) {
              // Replace selection
              setSelectedDrawingIds([hitId]);
            }
            // If already selected and not shift, do nothing (wait for drag)

            useBattlemapStore.getState().setIsDraggingDrawing(true);
            dragStartPosRef.current = { x, y };
          } else {
            // Clicked on empty space
            if (!isShift) {
              setSelectedDrawingIds([]);
            }
            // Clear any existing preview before starting new marquee
            if (previewGraphicsRef.current) {
              previewGraphicsRef.current.clear();
              previewGraphicsRef.current.visible = true;
            }
            // Start Marquee - always free selection (no snap) as requested
            marqueeStartRef.current = { x, y };
            // Set marquee active to hide selection overlays from useDrawingRenderer
            useBattlemapStore.getState().setIsMarqueeActive(true);
            // Dispatch event for immediate visibility toggle (bypasses React render cycle)
            window.dispatchEvent(
              new CustomEvent("battlemap:marquee-toggle", {
                detail: { active: true },
              })
            );
          }

          // Note: Right-click context menu is handled before this point due to early return
        }
        return;
      } else if (activeTool === "text") {
        // Legacy text tool - removed functionality or mapped to drawTool 'text'
        // keeping mostly empty or redirecting logic if needed, but tool is removed from toolbar
      }

      // Update preview immediately on down
      updatePreview(x, y, shouldSnap);
    },
    [
      viewport,
      doc,
      fogArray,
      drawingsArray,
      wallsArray,
      roomsArray,
      settings,
      previewGraphicsRef,
      activeTool,
      fogTool,
      drawTool,
      fogMode,
      activeRoomId,
      isDrawing,
      setIsDrawing,
      setCurrentPath,
      appendToCurrentPath,
      currentPath,
      clearCurrentPath,
      updatePreview,
      onOpenContextMenu,
      setSelectedDrawingIds, // Added
      selectedDrawingIds, // Added
    ]
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const {
        isDraggingDrawing,
        isDrawing,
        activeTool,
        selectedDrawingIds,
        fogTool,
        fogMode,
        drawTool,
        brushSize,
        setIsDrawing,
        currentPath,
        clearCurrentPath,
        setSelectedDrawingIds,
        setIsDraggingDrawing,
        drawingProps,
        settings,
        setActiveTool,
      } = useBattlemapStore.getState();

      if (activeTool === "select") {
        // FINISH MARQUEE SELECTION
        if (marqueeStartRef.current && viewport) {
          const start = marqueeStartRef.current;

          // Fix: Use client coordinates mapped to canvas for window-attached event
          const canvas = app?.canvas as HTMLCanvasElement;
          const rect = canvas?.getBoundingClientRect();
          const clientX = e.clientX;
          const clientY = e.clientY;
          const x = clientX - (rect?.left || 0);
          const y = clientY - (rect?.top || 0);

          const end = viewport.toLocal({ x, y });

          // Apply snapping if enabled (Ctrl inverts behavior)
          const shouldSnap = e.ctrlKey
            ? !settings.snapToGrid
            : settings.snapToGrid;
          let finalX = end.x;
          let finalY = end.y;
          if (shouldSnap) {
            const snapped = snapToGridCorner(
              end.x,
              end.y,
              settings.gridType,
              settings.gridCellSize,
              settings.gridOffsetX || 0,
              settings.gridOffsetY || 0
            );
            finalX = snapped.x;
            finalY = snapped.y;
          }

          // Check if it was a drag (tolerance)
          if (Math.hypot(finalX - start.x, finalY - start.y) > DRAG_TOLERANCE) {
            const bounds = {
              minX: Math.min(start.x, finalX),
              maxX: Math.max(start.x, finalX),
              minY: Math.min(start.y, finalY),
              maxY: Math.max(start.y, finalY),
            };

            // Find all intersections using marquee select utility
            const hits = marqueeSelect(bounds, drawingsArray.toArray());

            // If shift, toggle/add? For Marquee, usually ADD or REPLACE.
            // Let's standard: If shift, ADD. Else REPLACE.
            const isShift = e.shiftKey;
            if (isShift) {
              // Union
              const set = new Set([...selectedDrawingIds, ...hits]);
              setSelectedDrawingIds(Array.from(set));
            } else {
              setSelectedDrawingIds(hits);
            }
          }

          // Cleanup
          marqueeStartRef.current = null;
          useBattlemapStore.getState().setIsMarqueeActive(false);
          // Dispatch event for immediate visibility toggle
          window.dispatchEvent(
            new CustomEvent("battlemap:marquee-toggle", {
              detail: { active: false },
            })
          );
          if (previewGraphicsRef.current) {
            previewGraphicsRef.current.clear();
          }
          // Force redraw to ensure cleared
          updatePreview(0, 0); // Coordinates don't matter since it will clear
        }

        // Always set pressed to false on up if left button
        if (e.button === 0) {
          isSelectButtonPressedRef.current = false;
        }

        setIsDraggingDrawing(false);
        dragStartPosRef.current = null;
        resizeHandleRef.current = null;
        initialResizeStateRef.current = null;
        return; // Stop here
      }

      if (!isDrawing && !isDraggingDrawing) return;

      if (activeTool === "fog") {
        if (fogTool === "rect" || fogTool === "ellipse") {
          const { x, y, width, height } = normalizeRect(
            currentPath[0],
            currentPath[1],
            currentPath[2],
            currentPath[3]
          );

          if (width > 1 && height > 1) {
            doc.transact(() => {
              fogArray.push([
                {
                  id: uuidv7(),
                  type: fogTool,
                  data: [x, y, width, height],
                  operation: fogMode === "hide" ? "add" : "sub",
                },
              ]);
            });
          }
        } else if (fogTool === "brush" && currentPath.length >= 4) {
          doc.transact(() => {
            fogArray.push([
              {
                id: uuidv7(),
                type: "brush",
                data: [...currentPath],
                operation: fogMode === "hide" ? "add" : "sub",
                width: brushSize,
              },
            ]);
          });
        } else if (fogTool === "polygon") {
          // If Shift is pressed, keep adding points (return early)
          if (e.shiftKey) return;

          // If finishing (Shift released), push the shape if valid
          if (currentPath.length >= POLYGON_MIN_POINTS) {
            doc.transact(() => {
              fogArray.push([
                {
                  id: uuidv7(),
                  type: "poly",
                  data: [...currentPath],
                  operation: fogMode === "hide" ? "add" : "sub",
                },
              ]);
            });
            // Force finish
            setIsDrawing(false);
            clearCurrentPath();
            if (previewGraphicsRef.current) previewGraphicsRef.current.clear();
          }
        }
      } else if (activeTool === "draw" && isDrawing) {
        if (drawTool === "brush" && currentPath.length >= 4) {
          const newId = uuidv7();
          const { strokeColor, strokeWidth, opacity, blur } = drawingProps;

          doc.transact(() => {
            drawingsArray.push([
              {
                id: newId,
                type: "brush",
                points: [...currentPath],
                strokeColor: strokeColor || DEFAULT_STROKE_COLOR,
                strokeWidth: strokeWidth || DEFAULT_STROKE_WIDTH,
                opacity: opacity ?? 1,
                blur: blur ?? 0,
                layer: settings.activeLayerId || "map",
                x: 0,
                y: 0,
              },
            ]);
          });
          // Auto Switch
          setActiveTool("select");
          setSelectedDrawingIds([newId]);
          setIsDrawing(false);
          clearCurrentPath();
        } else if (drawTool === "rect" || drawTool === "ellipse") {
          const { x, y, width, height } = normalizeRect(
            currentPath[0],
            currentPath[1],
            currentPath[2],
            currentPath[3]
          );

          if (width > MIN_RECT_SIZE && height > MIN_RECT_SIZE) {
            // Min size check
            const newId = uuidv7();
            const {
              strokeColor,
              strokeWidth,
              fillColor,
              fillAlpha,
              opacity,
              blur,
            } = drawingProps;

            doc.transact(() => {
              drawingsArray.push([
                {
                  id: newId,
                  type: drawTool, // "rect" or "ellipse"
                  x,
                  y,
                  width,
                  height,
                  strokeColor: strokeColor || DEFAULT_STROKE_COLOR,
                  strokeWidth: strokeWidth || DEFAULT_STROKE_WIDTH,
                  fillColor: fillColor || DEFAULT_FILL_COLOR,
                  fillAlpha: fillAlpha ?? DEFAULT_FILL_ALPHA,
                  opacity: opacity ?? 1,
                  blur: blur ?? 0,
                  layer: settings.activeLayerId || "map",
                } as DrawingShape,
              ]);
            });
            // Auto Switch
            setActiveTool("select");
            setSelectedDrawingIds([newId]);
            setIsDrawing(false);
            clearCurrentPath();
          }
        } else if (drawTool === "polygon") {
          // Polygon finishes on double click or closing loop
          if (e.shiftKey) return;
        }
      }

      // Default clearance for non-polygon tools (or failed polygon)
      // Don't reset for wall tool - it has its own drawing logic
      const shouldClear =
        activeTool !== "wall" &&
        (activeTool !== "fog" || fogTool !== "polygon") &&
        (activeTool !== "draw" || drawTool !== "polygon");

      if (shouldClear) {
        setIsDrawing(false);
        clearCurrentPath();
        if (previewGraphicsRef.current) previewGraphicsRef.current.clear();
      }
    },
    [
      doc,
      viewport,
      drawingsArray,
      fogArray,
      wallsArray,
      roomsArray,
      previewGraphicsRef,
      updatePreview,
      isDraggingDrawing,
      isDrawing,
      activeTool,
      selectedDrawingIds,
      fogTool,
      fogMode,
      drawTool,
      brushSize,
      setIsDrawing,
      currentPath,
      clearCurrentPath,
      setSelectedDrawingIds,
      setIsDraggingDrawing,
      drawingProps,
      settings,
      setActiveTool,
    ]
  );

  // GLOBAL POINTER UP FIX
  useEffect(() => {
    const handleGlobalUp = () => {
      const { isDraggingDrawing } = useBattlemapStore.getState();
      if (isDraggingDrawing) {
        useBattlemapStore.getState().setIsDraggingDrawing(false);
        useBattlemapStore.getState().setIsMarqueeActive(false);
        // Dispatch event for immediate visibility toggle
        window.dispatchEvent(
          new CustomEvent("battlemap:marquee-toggle", {
            detail: { active: false },
          })
        );
        dragStartPosRef.current = null;
        resizeHandleRef.current = null;
        initialResizeStateRef.current = null;
        isSelectButtonPressedRef.current = false;
        marqueeStartRef.current = null;
        const g = previewGraphicsRef.current;
        if (g) {
          g.clear();
        }
      }
    };

    window.addEventListener("pointerup", handleGlobalUp);
    return () => window.removeEventListener("pointerup", handleGlobalUp);
  }, [previewGraphicsRef]); // Run once

  const onDoubleClick = useCallback(
    (e: PointerEvent) => {
      if (!viewport) return;
      const {
        activeTool,
        drawTool,
        isDrawing,
        currentPath,
        setIsDrawing,
        clearCurrentPath,
        drawingProps,
        settings,
        setActiveTool,
        setSelectedDrawingIds,
        setEditingItemId,
      } = useBattlemapStore.getState();

      const point = viewport.toLocal({
        x: e.offsetX,
        y: e.offsetY,
        clientX: e.clientX,
        clientY: e.clientY,
      } as any);

      if (activeTool === "draw" && drawTool === "polygon" && isDrawing) {
        // Finish Polygon
        if (currentPath.length >= POLYGON_MIN_POINTS) {
          const {
            strokeColor,
            strokeWidth,
            fillColor,
            fillAlpha,
            opacity,
            blur,
          } = drawingProps;

          const newId = uuidv7();
          doc.transact(() => {
            drawingsArray.push([
              {
                id: newId,
                type: "polygon",
                points: [...currentPath],
                x: 0,
                y: 0,
                strokeColor: strokeColor || DEFAULT_STROKE_COLOR,
                strokeWidth: strokeWidth || DEFAULT_STROKE_WIDTH,
                fillColor: fillColor || DEFAULT_FILL_COLOR,
                fillAlpha: fillAlpha ?? DEFAULT_DRAW_FILL_ALPHA,
                opacity: opacity ?? 1,
                blur: blur ?? 0,
                layer: settings.activeLayerId || "map",
              },
            ]);
          });
          // Auto Switch
          setActiveTool("select");
          setSelectedDrawingIds([newId]);
          setIsDrawing(false);
          clearCurrentPath();
          if (previewGraphicsRef.current) previewGraphicsRef.current.clear();
        }
      } else if (activeTool === "select" || activeTool === "draw") {
        // Check for text editing
        const hitText = drawingsArray.toArray().find((d) => {
          if (d.type !== "text") return false;
          // Use hit testing utility
          return pointInDrawing(point.x, point.y, d);
        });

        if (hitText) {
          setEditingItemId(hitText.id);
        }
      }
    },
    [
      viewport,
      doc,
      drawingsArray,
      previewGraphicsRef,
      activeTool,
      drawTool,
      isDrawing,
      currentPath,
      setIsDrawing,
      clearCurrentPath,
      drawingProps,
      settings,
      setActiveTool,
      setSelectedDrawingIds,
      setEditingItemId,
    ]
  );

  const onPointerLeave = useCallback(
    (_event: PointerEvent) => {
      if (previewGraphicsRef.current) previewGraphicsRef.current.clear();
    },
    [previewGraphicsRef]
  );

  // Middle mouse button handler
  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button === 1) {
        // Middle button
        e.preventDefault();
        e.stopPropagation();

        // Enable temporary pan mode (updates toolbar)
        const { enableTemporaryPan } = useBattlemapStore.getState();
        enableTemporaryPan();

        isMiddlePressedRef.current = true;
        middleDragStartRef.current = { x: e.clientX, y: e.clientY };

        // Change cursor to indicate pan mode
        if (app?.canvas) {
          (app.canvas as HTMLCanvasElement).style.cursor = "grab";
        }
      }
    },
    [app]
  );

  const onMouseUp = useCallback(
    (e: MouseEvent) => {
      if (e.button === 1) {
        // Middle button

        // Disable temporary pan mode (restore previous tool in toolbar)
        const { disableTemporaryPan } = useBattlemapStore.getState();
        disableTemporaryPan();

        isMiddlePressedRef.current = false;
        middleDragStartRef.current = null;

        // Restore cursor
        if (app?.canvas) {
          (app.canvas as HTMLCanvasElement).style.cursor = "default";
        }
      }
    },
    [app]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!viewport) return;

      const {
        isDraggingDrawing,
        activeTool,
        fogTool,
        drawTool,
        isDrawing,
        currentPath,
        appendToCurrentPath,
        setCurrentPath,
      } = useBattlemapStore.getState();

      // Handle middle button drag manually
      if (isMiddlePressedRef.current && middleDragStartRef.current) {
        const dx = e.clientX - middleDragStartRef.current.x;
        const dy = e.clientY - middleDragStartRef.current.y;

        // Update viewport position
        // eslint-disable-next-line react-compiler/react-compiler
        viewport.x += dx;
        // eslint-disable-next-line react-compiler/react-compiler
        viewport.y += dy;

        // Update last position
        middleDragStartRef.current = { x: e.clientX, y: e.clientY };
        return; // Don't process other interactions during middle drag
      }

      const point = viewport.toLocal({
        x: e.offsetX,
        y: e.offsetY,
        clientX: e.clientX,
        clientY: e.clientY,
      } as any);
      let { x, y } = point;

      // Snap logic (Magnetic Snap)
      const shouldSnap = e.ctrlKey ? !settings.snapToGrid : settings.snapToGrid;
      if (shouldSnap) {
        if (activeTool === "fog" && fogTool !== "grid" && fogTool !== "fill") {
          // For rect/ellipse, snap to grid corner (top-left) instead of center
          if (fogTool === "rect" || fogTool === "ellipse") {
            const snapped = snapToGridCorner(
              x,
              y,
              settings.gridType,
              settings.gridCellSize,
              settings.gridOffsetX || 0,
              settings.gridOffsetY || 0
            );
            x = snapped.x;
            y = snapped.y;
          } else {
            const snapped = GridRenderer.snapToGrid(
              x,
              y,
              settings.gridType,
              settings.gridCellSize,
              settings.gridOffsetX || 0,
              settings.gridOffsetY || 0
            );
            x = snapped.x;
            y = snapped.y;
          }
        } else if (
          activeTool === "draw" &&
          (drawTool === "rect" || drawTool === "ellipse")
        ) {
          const snapped = snapToGridCorner(
            x,
            y,
            settings.gridType,
            settings.gridCellSize,
            settings.gridOffsetX || 0,
            settings.gridOffsetY || 0
          );
          x = snapped.x;
          y = snapped.y;
        }
      }

      // Update Preview (Always, for hover cursors etc)
      // updatePreview handles marquee drawing when activeTool is "select"
      if (!isDraggingDrawing) {
        updatePreview(x, y, shouldSnap);
      } else if (previewGraphicsRef.current) {
        // When dragging drawings, clear the preview
        previewGraphicsRef.current.clear();
      }

      if (!isDrawing && !isDraggingDrawing) return;

      if (activeTool === "fog") {
        if (fogTool === "brush") {
          const lastX = currentPath[currentPath.length - 2];
          const lastY = currentPath[currentPath.length - 1];
          // Optimization: don't add too many points
          if (
            currentPath.length < 2 ||
            Math.hypot(x - lastX, y - lastY) > BRUSH_MIN_DISTANCE
          ) {
            appendToCurrentPath(x, y);
          }
        } else if (fogTool === "rect" || fogTool === "ellipse") {
          const startX = currentPath[0];
          const startY = currentPath[1];
          // Update currentPath with snapped end position for preview
          setCurrentPath([startX, startY, x, y]);
        }
      } else if (activeTool === "draw") {
        // Snap logic for draw tool
        const shouldSnapDraw = e.ctrlKey
          ? !settings.snapToGrid
          : settings.snapToGrid;
        let snapX = x;
        let snapY = y;
        if (shouldSnapDraw && (drawTool === "rect" || drawTool === "ellipse")) {
          const snapped = snapToGridCorner(
            x,
            y,
            settings.gridType,
            settings.gridCellSize,
            settings.gridOffsetX || 0,
            settings.gridOffsetY || 0
          );
          snapX = snapped.x;
          snapY = snapped.y;
        }

        if (drawTool === "brush") {
          // Optimization logic akin to fog brush
          const lastX = currentPath[currentPath.length - 2];
          const lastY = currentPath[currentPath.length - 1];
          if (
            currentPath.length < 2 ||
            Math.hypot(x - lastX, y - lastY) > DRAW_BRUSH_MIN_DISTANCE
          ) {
            appendToCurrentPath(x, y);
          }
        } else if (drawTool === "rect" || drawTool === "ellipse") {
          const startX = currentPath[0];
          const startY = currentPath[1];
          setCurrentPath([startX, startY, snapX, snapY]);
        }
      } else if (activeTool === "select") {
        const { selectedDrawingIds } = useBattlemapStore.getState();
        if (selectedDrawingIds.length > 0 && dragStartPosRef.current) {
          // RESIZE Logic
          if (
            resizeHandleRef.current &&
            initialResizeStateRef.current &&
            selectedDrawingIds.length === 1
          ) {
            const selectedDrawingId = selectedDrawingIds[0];
            const dx = x - dragStartPosRef.current.x;
            const dy = y - dragStartPosRef.current.y;
            const init = initialResizeStateRef.current;
            const handle = resizeHandleRef.current;
            const startPos = dragStartPosRef.current;

            doc.transact(() => {
              const items = drawingsArray.toArray();
              const index = items.findIndex((d) => d.id === selectedDrawingId);
              if (index !== -1) {
                const item = items[index];
                if (item.type === "rect" || item.type === "ellipse") {
                  let newX = init.x;
                  let newY = init.y;
                  let newW = init.width;
                  let newH = init.height;

                  if (
                    handle.includes(HANDLE_TOP_LEFT) ||
                    handle.includes(HANDLE_BOTTOM_LEFT)
                  ) {
                    newX += dx;
                    newW -= dx;
                  }
                  if (
                    handle.includes(HANDLE_TOP_RIGHT) ||
                    handle.includes(HANDLE_BOTTOM_RIGHT)
                  ) {
                    newW += dx;
                  }
                  if (
                    handle.includes(HANDLE_TOP_LEFT) ||
                    handle.includes(HANDLE_TOP_RIGHT)
                  ) {
                    newY += dy;
                    newH -= dy;
                  }
                  if (
                    handle.includes(HANDLE_BOTTOM_LEFT) ||
                    handle.includes(HANDLE_BOTTOM_RIGHT)
                  ) {
                    newH += dy;
                  }

                  // Prevent negative size
                  if (newW < MIN_SHAPE_SIZE) newW = MIN_SHAPE_SIZE;
                  if (newH < MIN_SHAPE_SIZE) newH = MIN_SHAPE_SIZE;

                  const newItem = {
                    ...item,
                    x: newX,
                    y: newY,
                    width: newW,
                    height: newH,
                  };

                  drawingsArray.delete(index, 1);
                  drawingsArray.insert(index, [newItem]);
                } else if (item.type === "text") {
                  let newY = init.y;
                  let newH = init.height;

                  // Text primarily resizes by font size (Height)
                  // width follows content
                  if (
                    handle.includes(HANDLE_TOP_LEFT) ||
                    handle.includes(HANDLE_TOP_RIGHT)
                  ) {
                    newY += dy;
                    newH -= dy;
                  }
                  if (
                    handle.includes(HANDLE_BOTTOM_LEFT) ||
                    handle.includes(HANDLE_BOTTOM_RIGHT)
                  ) {
                    newH += dy;
                  }

                  if (newH < MIN_SHAPE_SIZE) newH = MIN_SHAPE_SIZE;

                  // Update font size based on height ratio (height = fontSize * TEXT_HEIGHT_RATIO)
                  const newFontSize = newH / TEXT_HEIGHT_RATIO;

                  // Width resize (wrapping) logic
                  let newW = init.width;
                  let finalX = init.x;

                  if (
                    handle.includes(HANDLE_TOP_LEFT) ||
                    handle.includes(HANDLE_BOTTOM_LEFT)
                  ) {
                    const dx = x - startPos.x;
                    newW = Math.max(MIN_TEXT_WIDTH, init.width - dx);
                    finalX = init.x + dx;
                  }
                  if (
                    handle.includes(HANDLE_TOP_RIGHT) ||
                    handle.includes(HANDLE_BOTTOM_RIGHT)
                  ) {
                    const dx = x - startPos.x;
                    newW = Math.max(MIN_TEXT_WIDTH, init.width + dx);
                  }

                  const newItem = {
                    ...item,
                    y: newY,
                    x: finalX,
                    width: newW,
                    fontSize: newFontSize,
                  };

                  drawingsArray.delete(index, 1);
                  drawingsArray.insert(index, [newItem]);
                }
              }
            });
          } else {
            // MOVE Logic (Multi-select support)
            const dx = x - dragStartPosRef.current.x;
            const dy = y - dragStartPosRef.current.y;

            // Move logic: Update ref to avoid accumulation
            dragStartPosRef.current = { x, y };

            doc.transact(() => {
              const items = drawingsArray.toArray();

              // Map items by ID for quick check/update if needed, or just iterate
              // Since we need to delete index and insert, working with the array list is best.
              // Careful with indices if we were deleting diverse items, but we replace 1-for-1.

              // We need to match IDs to current indices safely.
              const idToIndex = new Map<string, number>();
              items.forEach((d, i) => idToIndex.set(d.id, i));

              selectedDrawingIds.forEach((id) => {
                const index = idToIndex.get(id);
                if (index !== undefined) {
                  const item = items[index];
                  const newItem = { ...item };

                  // Move logic depends on type
                  if (newItem.type === "polygon" || newItem.type === "brush") {
                    // Points are absolute
                    newItem.points = newItem.points.map((v, i) =>
                      i % 2 === 0 ? v + dx : v + dy
                    );
                  } else {
                    // Rect, Ellipse, Text use x,y
                    newItem.x += dx;
                    newItem.y += dy;
                  }

                  // Perform the update
                  drawingsArray.delete(index, 1);
                  drawingsArray.insert(index, [newItem]);
                }
              });
            });
          }
        }
      }
    },
    [viewport, previewGraphicsRef, updatePreview, doc, drawingsArray, settings]
  );

  // Use refs to store latest handlers to avoid re-attaching listeners on every render
  const onPointerDownRef = useRef(onPointerDown);
  const onPointerMoveRef = useRef(onPointerMove);
  const onPointerLeaveRef = useRef(onPointerLeave);
  const onMouseDownRef = useRef(onMouseDown);
  const onDoubleClickRef = useRef(onDoubleClick);
  const onMouseUpRef = useRef(onMouseUp);
  const onPointerUpRef = useRef(onPointerUp);

  // Update refs when handlers change
  useEffect(() => {
    onPointerDownRef.current = onPointerDown;
    onPointerMoveRef.current = onPointerMove;
    onPointerLeaveRef.current = onPointerLeave;
    onMouseDownRef.current = onMouseDown;
    onDoubleClickRef.current = onDoubleClick;
    onMouseUpRef.current = onMouseUp;
    onPointerUpRef.current = onPointerUp;
  });

  // Attach to Canvas - only when app/viewport change
  useEffect(() => {
    if (!app?.canvas || !viewport) return;
    const canvas = app.canvas as HTMLCanvasElement;
    console.log("Attaching Battlemap Interactions to Canvas");

    // Stable wrapper functions that call latest handlers via refs
    const handlePointerDown = (e: PointerEvent) => onPointerDownRef.current(e);
    const handlePointerMove = (e: PointerEvent) => onPointerMoveRef.current(e);
    const handlePointerLeave = (e: PointerEvent) =>
      onPointerLeaveRef.current(e);
    const handleMouseDown = (e: MouseEvent) => onMouseDownRef.current(e);
    const handleDoubleClick = (e: MouseEvent) =>
      onDoubleClickRef.current(e as unknown as PointerEvent);
    const handleMouseUp = (e: MouseEvent) => onMouseUpRef.current(e);
    const handlePointerUp = (e: PointerEvent) => onPointerUpRef.current(e);

    canvas.addEventListener("pointerdown", handlePointerDown);
    canvas.addEventListener("pointermove", handlePointerMove);
    canvas.addEventListener("pointerleave", handlePointerLeave);
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("dblclick", handleDoubleClick);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown);
      canvas.removeEventListener("pointermove", handlePointerMove);
      canvas.removeEventListener("pointerleave", handlePointerLeave);
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("dblclick", handleDoubleClick);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [app, viewport]);
}

function doSegmentsIntersect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
): boolean {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (denom === 0) return false;

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

function isDoorOnBoundary(
  doorId: string,
  polygon: number[],
  segments: WallSegment[]
): boolean {
  const door = segments.find((s) => s.id === doorId);
  if (!door || !door.isDoor) return false;

  const n = polygon.length;
  for (let i = 0; i < n; i += 2) {
    const x1 = polygon[i];
    const y1 = polygon[i + 1];
    const x2 = polygon[(i + 2) % n];
    const y2 = polygon[(i + 3) % n];

    if (
      doSegmentsIntersect(door.x1, door.y1, door.x2, door.y2, x1, y1, x2, y2)
    ) {
      return true;
    }
  }
  return false;
}

export function linkDoorsToRoom(
  roomId: string,
  roomBounds: number[],
  wallsArray: Y.Array<Wall> | null,
  roomsArray: Y.Array<FogRoom>,
  doc: Y.Doc
) {
  if (!wallsArray) return;

  const segments: WallSegment[] = [];
  wallsArray.forEach((w) => segments.push(...w.segments));

  const doorIds: string[] = [];
  segments.forEach((seg) => {
    if (seg.isDoor && isDoorOnBoundary(seg.id, roomBounds, segments)) {
      doorIds.push(seg.id);
    }
  });

  if (doorIds.length === 0) return;

  doc.transact(() => {
    const rooms = roomsArray.toArray();
    const index = rooms.findIndex((r) => r.id === roomId);
    if (index !== -1) {
      const room = rooms[index];
      const existingDoorIds = room.doorIds || [];
      const uniqueDoorIds = [...new Set([...existingDoorIds, ...doorIds])];
      const updated = { ...room, doorIds: uniqueDoorIds };
      roomsArray.delete(index, 1);
      roomsArray.insert(index, [updated]);
    }
  });
}
